/**
 * Phrases, placeholders and offers: everything the bot says that is not tied to one tool.
 *
 * Three tabs rather than three nav entries, because they are read together. A placeholder exists
 * to be used in a message; a phrase is what the bot falls back to when no tool applies; an offer
 * rides a reply. Splitting them across the sidebar would mean three clicks to answer "why is this
 * placeholder red?".
 *
 * ## Phrases are listed by key, and the keys are not arbitrary
 *
 * Each one is a code path — `unclear` is what an unrecognised message gets, `otp_in_chat` is
 * golden rule 11's answer, each refusal reason is a key. The screen says so beside each, because
 * "what is `out_of_scope` for?" is otherwise a question only the source answers.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Button,
  Card,
  EmptyState,
  Input,
  Select,
  StatusPill,
  Tabs,
  Textarea,
  deleteOffer,
  deletePlaceholder,
  directionOfText,
  saveOffer,
  savePhrase,
  savePlaceholder,
  type BotOffer,
  type BotPlaceholder,
} from "@nab/shared";

import { tagsOf, useBot } from "../config";
import { VersionBar } from "./VersionBar";

type TabId = "phrases" | "placeholders" | "offers";

export function ContentPage(): JSX.Element {
  const { t } = useTranslation("app");
  const { config, problem } = useBot();
  const [tab, setTab] = useState<TabId>("phrases");

  if (problem) return <EmptyState title={t("notReady")} description={problem} />;

  return (
    <div className="flex flex-col">
      <VersionBar />
      <div className="flex flex-col gap-4 p-4">
        <Tabs
          label={t("content.tabs")}
          active={tab}
          onChange={(id) => setTab(id as TabId)}
          tabs={[
            { id: "phrases", label: t("content.phrases") },
            { id: "placeholders", label: t("content.placeholders") },
            { id: "offers", label: t("content.offers") },
          ]}
        />
        {tab === "phrases" ? <Phrases /> : null}
        {tab === "placeholders" ? <Placeholders /> : null}
        {tab === "offers" ? <Offers /> : null}
        {config ? null : <p className="text-body-sm text-textMuted">{t("loading")}</p>}
      </div>
    </div>
  );
}

// -- Phrases -----------------------------------------------------------------------------

function Phrases(): JSX.Element {
  const { t } = useTranslation("app");
  const { config, save } = useBot();
  const tags = tagsOf(config);
  const phrases = config?.phrases ?? {};

  return (
    <div className="flex flex-col gap-4">
      {Object.keys(phrases)
        .sort()
        .map((key) => (
          <Card key={key} className="flex flex-col gap-3 p-4">
            <div>
              <h3 className="font-mono text-body-md">{key}</h3>
              {/* What this phrase is for. Each key is a branch in the engine, and a screen that
                  only showed the key would leave "when does a customer see this?" unanswerable
                  without reading the source. */}
              <p className="text-body-sm text-textMuted">{t(`phrase.${key}`, "")}</p>
            </div>
            <PhraseEditor
              phraseKey={key}
              tags={tags}
              texts={phrases[key] ?? {}}
              onSave={(texts) =>
                save(() => savePhrase(key, texts), t("content.phraseSaved", { key }))
              }
            />
          </Card>
        ))}
    </div>
  );
}

function PhraseEditor({
  phraseKey,
  tags,
  texts,
  onSave,
}: {
  phraseKey: string;
  tags: string[];
  texts: Record<string, string>;
  onSave: (texts: Record<string, string>) => void;
}): JSX.Element {
  const { t } = useTranslation("app");
  const [draft, setDraft] = useState(texts);
  const dirty = JSON.stringify(draft) !== JSON.stringify(texts);

  return (
    <>
      {tags.map((tag) => (
        <Textarea
          key={tag}
          label={tag}
          rows={2}
          dir={directionOfText(draft[tag] ?? "")}
          value={draft[tag] ?? ""}
          onChange={(event) => setDraft({ ...draft, [tag]: event.target.value })}
        />
      ))}
      <div>
        <Button
          size="sm"
          variant="primary"
          disabled={!dirty}
          onClick={() => onSave(draft)}
          aria-label={t("content.savePhrase", { key: phraseKey })}
        >
          {t("tools.save")}
        </Button>
      </div>
    </>
  );
}

// -- Placeholders ------------------------------------------------------------------------

function Placeholders(): JSX.Element {
  const { t } = useTranslation("app");
  const { config, save } = useBot();
  const placeholders = config?.placeholders ?? [];

  // Where each one is used, computed here rather than stored: the answer changes every time a
  // message is edited, and a stored copy would be wrong more often than right.
  const usage = new Map<string, string[]>();
  for (const tool of config?.tools ?? []) {
    for (const [outcome, texts] of Object.entries(tool.messages ?? {})) {
      for (const text of Object.values(texts)) {
        for (const match of String(text).matchAll(
          /\{\{\s*([A-Za-z_][A-Za-z0-9_]*)\s*\}\}/g,
        )) {
          const name = match[1]!;
          const where = `${tool.action}.${outcome}`;
          const list = usage.get(name) ?? [];
          if (!list.includes(where)) list.push(where);
          usage.set(name, list);
        }
      }
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="max-w-prose text-body-sm text-textMuted">
        {t("placeholders.hint")}
      </p>
      {placeholders.map((placeholder) => (
        <PlaceholderCard
          key={placeholder.name}
          placeholder={placeholder}
          usedIn={usage.get(placeholder.name) ?? []}
          onSave={(next) =>
            save(
              () => savePlaceholder(next),
              t("placeholders.saved", { name: next.name }),
            )
          }
          onDelete={() =>
            save(
              () => deletePlaceholder(placeholder.name),
              t("placeholders.deleted", { name: placeholder.name }),
            )
          }
        />
      ))}
      <Button
        variant="secondary"
        onClick={() =>
          save(
            () =>
              savePlaceholder({
                name: `placeholder${placeholders.length + 1}`,
                type: "string",
                description: "",
                example: "",
              }),
            t("placeholders.added"),
          )
        }
      >
        {t("placeholders.add")}
      </Button>
    </div>
  );
}

const PLACEHOLDER_TYPES = [
  "string",
  "money",
  "date",
  "datetime",
  "percent",
  "list",
  "number",
];

function PlaceholderCard({
  placeholder,
  usedIn,
  onSave,
  onDelete,
}: {
  placeholder: BotPlaceholder;
  usedIn: string[];
  onSave: (next: BotPlaceholder) => void;
  onDelete: () => void;
}): JSX.Element {
  const { t } = useTranslation("app");
  const [draft, setDraft] = useState(placeholder);
  const dirty = JSON.stringify(draft) !== JSON.stringify(placeholder);

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex flex-wrap items-end gap-3">
        <Input
          label={t("placeholders.name")}
          className="font-mono"
          value={draft.name}
          onChange={(event) => setDraft({ ...draft, name: event.target.value })}
        />
        <Select
          label={t("placeholders.type")}
          value={draft.type}
          onChange={(event) => setDraft({ ...draft, type: event.target.value })}
          options={PLACEHOLDER_TYPES.map((value) => ({ value, label: value }))}
        />
        <Input
          label={t("placeholders.example")}
          hint={t("placeholders.exampleHint")}
          value={draft.example}
          onChange={(event) => setDraft({ ...draft, example: event.target.value })}
        />
      </div>
      <Input
        label={t("placeholders.description")}
        value={draft.description}
        onChange={(event) => setDraft({ ...draft, description: event.target.value })}
      />
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-label-md text-textMuted">
          {t("placeholders.usedIn")}
        </span>
        {usedIn.length > 0 ? (
          usedIn.map((where) => (
            <StatusPill key={where} tone="neutral">
              {where}
            </StatusPill>
          ))
        ) : (
          <span className="text-body-sm text-textMuted">
            {t("placeholders.unused")}
          </span>
        )}
        <div className="ms-auto flex gap-2">
          <Button size="sm" variant="secondary" onClick={onDelete}>
            {t("placeholders.delete")}
          </Button>
          <Button size="sm" variant="primary" disabled={!dirty} onClick={() => onSave(draft)}>
            {t("tools.save")}
          </Button>
        </div>
      </div>
    </Card>
  );
}

// -- Offers ------------------------------------------------------------------------------

function Offers(): JSX.Element {
  const { t } = useTranslation("app");
  const { config, save } = useBot();
  const offers = config?.offers ?? [];
  const tags = tagsOf(config);

  return (
    <div className="flex flex-col gap-4">
      <p className="max-w-prose text-body-sm text-textMuted">{t("offers.hint")}</p>
      {offers.map((offer) => (
        <OfferCard
          key={offer.offerId}
          offer={offer}
          tags={tags}
          onSave={(next) =>
            save(() => saveOffer(next), t("offers.saved", { id: next.offerId }))
          }
          onDelete={() =>
            save(
              () => deleteOffer(offer.offerId),
              t("offers.deleted", { id: offer.offerId }),
            )
          }
        />
      ))}
      <Button
        variant="secondary"
        onClick={() =>
          save(
            () =>
              saveOffer({
                offerId: `off_new_${offers.length + 1}`,
                topics: [],
                approved: false,
                headline: {},
              }),
            t("offers.added"),
          )
        }
      >
        {t("offers.add")}
      </Button>
    </div>
  );
}

function OfferCard({
  offer,
  tags,
  onSave,
  onDelete,
}: {
  offer: BotOffer;
  tags: string[];
  onSave: (next: BotOffer) => void;
  onDelete: () => void;
}): JSX.Element {
  const { t } = useTranslation("app");
  const [draft, setDraft] = useState(offer);
  const dirty = JSON.stringify(draft) !== JSON.stringify(offer);

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex flex-wrap items-end gap-3">
        <Input
          label={t("offers.id")}
          className="font-mono"
          value={draft.offerId}
          onChange={(event) => setDraft({ ...draft, offerId: event.target.value })}
        />
        <Input
          label={t("offers.topics")}
          hint={t("offers.topicsHint")}
          value={draft.topics.join(", ")}
          onChange={(event) =>
            setDraft({
              ...draft,
              topics: event.target.value
                .split(",")
                .map((topic) => topic.trim())
                .filter(Boolean),
            })
          }
        />
        <Input
          label={t("offers.validFrom")}
          type="date"
          value={draft.validFrom ?? ""}
          onChange={(event) => setDraft({ ...draft, validFrom: event.target.value })}
        />
        <Input
          label={t("offers.validTo")}
          type="date"
          value={draft.validTo ?? ""}
          onChange={(event) => setDraft({ ...draft, validTo: event.target.value })}
        />
      </div>

      {tags.map((tag) => (
        <Input
          key={tag}
          label={`${t("offers.headline")} — ${tag}`}
          dir={directionOfText(draft.headline[tag] ?? "")}
          value={draft.headline[tag] ?? ""}
          onChange={(event) =>
            setDraft({ ...draft, headline: { ...draft.headline, [tag]: event.target.value } })
          }
        />
      ))}

      <Input
        label={t("offers.deepLink")}
        value={draft.deepLink ?? ""}
        onChange={(event) => setDraft({ ...draft, deepLink: event.target.value })}
      />

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-body-md">
          <input
            type="checkbox"
            checked={draft.approved}
            onChange={(event) => setDraft({ ...draft, approved: event.target.checked })}
          />
          {/* Approval is what lets an offer be shown at all, and changing it is one of the two
              things that needs a second pair of eyes before publishing. */}
          {t("offers.approved")}
        </label>
        <div className="ms-auto flex gap-2">
          <Button size="sm" variant="secondary" onClick={onDelete}>
            {t("offers.delete")}
          </Button>
          <Button size="sm" variant="primary" disabled={!dirty} onClick={() => onSave(draft)}>
            {t("tools.save")}
          </Button>
        </div>
      </div>
    </Card>
  );
}
